import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { fetchGrammarExercise, saveExerciseProgress, loadExerciseProgress, resetExerciseProgress, recordExerciseAttempt } from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';
import Loading from '../Loading';
import Error from '../Error';
import GrammarFillInExercise from './GrammarFillInExercise';
import Results from './Results';
import ExerciseLayout from '../shared/ExerciseLayout';
import { useAuth } from '../../contexts/AuthContext';

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const GrammarFillInOrchestrator = forwardRef(({ topic = 'groceries', onBack }, ref) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [wordOptions, setWordOptions] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [revealedAnswers, setRevealedAnswers] = useState(new Set());
  const [showResults, setShowResults] = useState(false);

  // Initialize timer for tracking
  const { stop: stopTimer } = useExerciseTimer();

  // Expose saveProgress method to parent component
  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      try {
        await saveExerciseProgress(topic, 'grammar_fillin', {
          currentExerciseIndex,
          userAnswers,
          revealedAnswers: [...revealedAnswers],
          showResults
        });
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  // Fetch exercises from API on component mount
  useEffect(() => {
    const loadExercises = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchGrammarExercise({ topic });

        setExercises(data.exercises);
        setWordOptions(shuffleArray(data.word_options));

      } catch (err) {
        console.error('Error fetching grammar exercises:', err);
        setError(err.message || 'Failed to load exercises');
      } finally {
        setLoading(false);
      }
    };

    loadExercises();
  }, [topic]);

  // Load saved progress after exercises are loaded
  useEffect(() => {
    const loadProgress = async () => {
      if (!loading && exercises.length > 0) {
        try {
          const savedProgress = await loadExerciseProgress(topic, 'grammar_fillin');
          if (savedProgress && savedProgress.progress_data) {
            if (savedProgress.progress_data.currentExerciseIndex !== undefined) {
              setCurrentExerciseIndex(savedProgress.progress_data.currentExerciseIndex);
            }
            if (savedProgress.progress_data.userAnswers) {
              setUserAnswers(savedProgress.progress_data.userAnswers);
            }
            if (savedProgress.progress_data.revealedAnswers) {
              setRevealedAnswers(new Set(savedProgress.progress_data.revealedAnswers));
            }
            if (savedProgress.progress_data.showResults) {
              setShowResults(true);
            }
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [loading, exercises.length, topic]);

  const handleReveal = (exerciseId) => {
    const exercise = exercises.find(ex => ex.id === exerciseId);
    if (!exercise) return;
    setRevealedAnswers(prev => new Set([...prev, exerciseId]));
    handleAnswerDrop(exerciseId, exercise.correct_answer);
  };

  const handleAnswerDrop = async (exerciseId, answer) => {
    const newUserAnswers = {
      ...userAnswers,
      [exerciseId]: answer
    };
    setUserAnswers(newUserAnswers);

    // Uložit progress ihned po výběru odpovědi
    try {
      await saveExerciseProgress(topic, 'grammar_fillin', {
        currentExerciseIndex,
        userAnswers: newUserAnswers,
        showResults: false
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const nextExercise = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      const nextIndex = currentExerciseIndex + 1;
      setCurrentExerciseIndex(nextIndex);
    }
  };

  const previousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
    }
  };

  const submitAnswers = async () => {
    // Stop timer and get final time
    const finalTime = stopTimer();

    // Calculate score — revealed answers don't count as correct
    const correctCount = exercises.filter(ex =>
      !revealedAnswers.has(ex.id) && userAnswers[ex.id] === ex.correct_answer
    ).length;
    const totalCount = exercises.length;

    // Collect errors — revealed answers are not shown as errors
    const errors = exercises
      .filter(ex => !revealedAnswers.has(ex.id) && userAnswers[ex.id] !== ex.correct_answer)
      .map(ex => ({
        category: 'grammar',
        exercise_id: ex.id,
        user_answer: userAnswers[ex.id],
        correct_answer: ex.correct_answer
      }));

    // Record exercise attempt (don't block UI if this fails)
    try {
      await recordExerciseAttempt({
        topic: topic,
        exercise_type: 'grammar_fillin',
        score: correctCount,
        max_score: totalCount,
        time_spent_seconds: finalTime,
        errors: errors.length > 0 ? errors : null
      });
    } catch (trackingError) {
      console.error('Failed to record exercise attempt:', trackingError);
      // Continue anyway - don't break user experience
    }

    setShowResults(true);

    // Save progress with showResults flag
    try {
      await saveExerciseProgress(topic, 'grammar_fillin', {
        currentExerciseIndex,
        userAnswers,
        revealedAnswers: [...revealedAnswers],
        showResults: true
      });
    } catch (err) {
      console.error('Error saving progress on submit:', err);
    }
  };

  const restart = async () => {
    setUserAnswers({});
    setRevealedAnswers(new Set());
    setCurrentExerciseIndex(0);
    setShowResults(false);

    // Reset progress in database
    try {
      await resetExerciseProgress(topic, 'grammar_fillin');
    } catch (err) {
      console.error('Error resetting progress:', err);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  // Loading state
  if (loading) {
    return <Loading />;
  }

  // Error state
  if (error) {
    return <Error error={error} />;
  }

  // Results view
  if (showResults) {
    return (
      <Results
        exercises={exercises}
        userAnswers={userAnswers}
        restart={restart}
        onBack={handleBack}
        topic={topic}
      />
    );
  }

  // Main exercise view
  return (
    <ExerciseLayout
      title="Gramatika"
      subtitle="Doplňte správná slova do vět"
      color="purple"
      icon="✓"
      progress={{ current: Object.keys(userAnswers).length, total: exercises.length }}
      onBack={handleBack}
    >
      <GrammarFillInExercise
        exercises={exercises}
        currentExerciseIndex={currentExerciseIndex}
        wordOptions={wordOptions}
        userAnswers={userAnswers}
        revealedAnswers={revealedAnswers}
        onAnswerDrop={handleAnswerDrop}
        onReveal={handleReveal}
        supportLevel={user?.support_level}
        onNext={nextExercise}
        onPrevious={previousExercise}
        onSubmit={submitAnswers}
        topic={topic}
      />
    </ExerciseLayout>
  );
});

export default GrammarFillInOrchestrator;
